import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import isNumber from 'services/numbers/isNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import { appendPlainTextToOfferText } from 'services/offers/offerText';
import translations from 'services/translations/translations';
import showFullTextOnHover from 'services/layout/showFullTextOnHover';
import { addMessage } from 'store/messages/actions';
import { removeNavMenuSubText, setNavMenuSubText } from 'store/navMenu/actions';
import { fetchInstallationCategories } from 'store/products/actions';
import { WRITE_PRODUCT_CONFIGURATIONS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';

import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import {
  DEFAULT_TARGET_MARGIN_PERCENT,
  priceVersionForTargetMargin,
} from './targetMargin';
import allFieldsAreFilled from './allFieldsAreFilled';
import AttachConfiguration from './attachConfiguration/AttachConfiguration';
import getDefaultVersion from './getDefaultVersion';
import Header from './Header';
import IFormState from '../IFormState';
import IInstallation from '../IInstallation';
import InstallationContract from './InstallationContract';
import Installations from './installations/Installations';
import ISubproduct from '../ISubproduct';
import IDetail from '../IDetail';
import ReferencePrices from './referencePrices/ReferencePrices';
import RestoreDeletedRows from './RestoreDeletedRows';
import {
  IDeletedRow,
  getSubproductName,
  restoreDetail,
  restoreInstallation,
  restoreSubproduct,
} from './deletedRows';
import IVersion from '../IVersion';
import Memo from './Memo';
import SaveAsConfiguration from './SaveAsConfiguration';
import formatToApiFormat from '../formatToApiFormat';
import OfferProductImages from '../OfferProductImages';
import OtherExpenses from './installations/OtherExpenses';
import Subproducts from './subproducts/Subproducts';
import Summary from './Summary';
import Version from './Version';
import VersionActions from './VersionActions';
import views from '../views';

import 'style/offers/CalculationWindow.css';
import BatchCalculationReviews from './batchCalculationReview/BatchCalculationReviews';
import ICalculationReview from '../ICalculationReview';

const CalculationWindow = ({
  close,
  formState,
  locked,
  offerProductId,
  offerType,
  openPreview,
  setFormState,
  setView,
  view,
  save,
}: {
  close: () => void;
  formState: IFormState;
  locked: boolean;
  // Undefined until the position is saved
  offerProductId?: number;
  // The type of the offer, e.g. preliminary-offer
  offerType?: string;
  // Opens the PDF preview, undefined when the offer has no preview yet
  openPreview?: () => void;
  setFormState: (formState: IFormState) => void;
  setView: (view: string) => void;
  view: string;
  save: (preventRedirect: boolean) => void;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.common, translations.offers]);
  const [memo, setMemo] = useState<string>('');
  const [versions, setVersions] = useState<IVersion[]>([getDefaultVersion()]);
  const [activeVersionIndex, setActiveVersionIndex] = useState<number>(0);
  const [saveVersion, setSaveVersion] = useState(false);
  // The alternative is saved as a product configuration by the users who may make them
  const canSaveConfigurations = useHasPermission(WRITE_PRODUCT_CONFIGURATIONS);
  const [saveAsConfigurationOpen, setSaveAsConfigurationOpen] = useState(false);
  // Shows row numbers in front of the rows for changing their order
  const [reordering, setReordering] = useState(false);
  const [deletedRows, setDeletedRows] = useState<IDeletedRow[]>([]);
  // The margin % the rows added in the window are priced for (see targetMargin.ts)
  const [targetMarginPercent, setTargetMarginPercent] = useState<string>(
    DEFAULT_TARGET_MARGIN_PERCENT
  );
  const nextDeletedRowId = useRef(0);
  const isReordering = reordering && !locked;
  const keepActiveVersion = useRef(false);
  // The number of alternatives in the form state the last time it changed
  const versionCount = useRef(0);

  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, []);

  // The position and the alternative being calculated stay visible in the header while
  // the window is scrolled, e.g. "VS1 EK - 66 - EK · Versio 2 (pääversio)"
  const activeVersion = versions[activeVersionIndex];
  const headerSubText = [
    `${formState.position} ${formState.name}`.trim(),
    // A price list row has no alternatives
    activeVersion === undefined || formState.isPriceListItem === true
      ? ''
      : `${t(`${translations.calculationWindow}:Version label`)} ${
          activeVersion.name
        }${
          activeVersion.isPrimary
            ? ` (${t(`${translations.calculationWindow}:primary`)})`
            : ''
        }`,
  ]
    .filter((part) => part !== '')
    .join(' · ');

  useEffect(() => {
    dispatch(setNavMenuSubText(headerSubText));
  }, [dispatch, headerSubText]);

  useEffect(
    () => () => {
      dispatch(removeNavMenuSubText());
    },
    [dispatch]
  );

  /*
   * The installation categories tell installations from other expenses. Until they are
   * loaded the installation sections would show no rows and 0 hours, so a loading text
   * is shown instead. The categories are a fixed list, so it is never empty once loaded.
   */
  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );
  const installationCategoriesLoaded =
    Array.isArray(installationCategories) && installationCategories.length > 0;
  const loadingInstallations = (
    <p className="calculation-loading">
      {t(`${translations.calculationWindow}:Loading installations`)}
    </p>
  );

  useEffect(() => {
    setMemo(formState.memo);

    setVersions(
      formState.versions.length > 0
        ? [...formState.versions]
        : [getDefaultVersion()]
    );

    const getPrimaryVersionIndex = () => {
      const index = formState.versions.findIndex(
        (version) => version.isPrimary
      );

      return index === -1 ? 0 : index;
    };

    // The primary version is opened when the position is loaded. When the calculation
    // window itself updates the form state (save), the user stays on the same version.
    // The saved alternatives get their ids from the server after a save: the same
    // alternatives, so the user stays on the same one
    const sameAlternatives =
      versionCount.current > 0 &&
      versionCount.current === formState.versions.length;
    versionCount.current = formState.versions.length;

    if (keepActiveVersion.current || sameAlternatives) {
      keepActiveVersion.current = false;
      setActiveVersionIndex((current) =>
        current < formState.versions.length ? current : getPrimaryVersionIndex()
      );
    } else {
      setActiveVersionIndex(getPrimaryVersionIndex());
    }

    if (saveVersion) {
      setSaveVersion(false);
      save(true);
    }
  }, [formState]);

  const onOfferTextChanged = (newText: string) => {
    const newActiveVersion = {
      ...versions[activeVersionIndex],
      offerText: newText,
    };

    const newVersions = [
      ...versions.slice(0, activeVersionIndex),
      newActiveVersion,
      ...versions.slice(activeVersionIndex + 1),
    ];

    setVersions(newVersions);
  };

  const setStateOfForm = () => {
    if (!allFieldsAreFilled(versions)) {
      dispatch(addMessage(t(`${translations.common}:Fill all fields`)));
      return;
    }
    keepActiveVersion.current = true;
    setFormState({
      ...formState,
      memo,
      versions,
    });
  };

  const ready = () => {
    setStateOfForm();

    if (allFieldsAreFilled(versions)) {
      close();
    }
  };

  const saveData = () => {
    setSaveVersion(true);
    setStateOfForm();
  };

  const setVersion = (updatedVersion: IVersion) =>
    setVersions(
      versions.map((version, index) =>
        index === activeVersionIndex ? updatedVersion : version
      )
    );

  const setInstallations = (installations: IInstallation[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      installations,
    });

  const setCalculationReviews = (calculationReviews: ICalculationReview[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      calculationReviews,
    });

  const setSubproducts = (subproducts: ISubproduct[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      subproducts,
    });

  const setSubproductsAndInstallations = (
    subproducts: ISubproduct[],
    installations: IInstallation[],
    offerText: string
  ) => {
    const newOfferText = appendPlainTextToOfferText(
      versions[activeVersionIndex].offerText,
      offerText
    );

    subproducts.forEach((sp) => {
      sp.details.forEach((detail) => {
        detail.expenseUnit =
          detail.expenseUnit === '' ||
          detail.expenseUnit === detail.quantityUnit
            ? detail.expenseUnit
            : detail.quantityUnit;
      });
    });

    setVersion({
      ...versions[activeVersionIndex],
      installations,
      subproducts,
      offerText: newOfferText,
    });
  };

  // Deleted rows can be restored while the calculation window is open, also after saving
  const getDeletedRowId = () => {
    nextDeletedRowId.current += 1;
    return nextDeletedRowId.current;
  };

  const keepDeletedSubproduct = (subproduct: ISubproduct, index: number) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'subproduct',
        row: subproduct,
        versionIndex: activeVersionIndex,
      },
    ]);

  const keepDeletedInstallation = (
    installation: IInstallation,
    index: number
  ) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'installation',
        row: installation,
        versionIndex: activeVersionIndex,
      },
    ]);

  const keepDeletedDetail = (
    subproduct: ISubproduct,
    detail: IDetail,
    index: number
  ) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'detail',
        parentKey: subproduct.key,
        parentName: getSubproductName(subproduct),
        row: detail,
        versionIndex: activeVersionIndex,
      },
    ]);

  const restoreDeletedRow = (deletedRow: IDeletedRow) => {
    const version = versions[activeVersionIndex];

    if (deletedRow.kind === 'subproduct') {
      setSubproducts(restoreSubproduct(version.subproducts, deletedRow));
    } else if (deletedRow.kind === 'installation') {
      setInstallations(restoreInstallation(version.installations, deletedRow));
    } else {
      const subproducts = restoreDetail(version.subproducts, deletedRow);
      if (subproducts === null) {
        dispatch(
          addMessage(
            t(
              `${translations.calculationWindow}:Restore the material row first`
            )
          )
        );
        return;
      }

      setSubproducts(subproducts);
    }

    setDeletedRows((rows) => rows.filter((row) => row.id !== deletedRow.id));
  };

  // A new target margin prices again the rows following it in all alternatives
  const handleTargetMarginChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setTargetMarginPercent(value);

    if (isNumber(value) && numericStringToFloat(value) < 100) {
      setVersions(
        versions.map((version) => priceVersionForTargetMargin(version, value))
      );
    }
  };

  const deletedRowsOfVersion = deletedRows.filter(
    (deletedRow) => deletedRow.versionIndex === activeVersionIndex
  );

  if (view === views.attachConfiguration) {
    return (
      <div className="calculation-window">
        <AttachConfiguration
          cancel={() => setView(views.calculationWindow)}
          formState={formState}
          locked={locked}
          installations={versions[activeVersionIndex].installations}
          setSubproductsAndInstallations={setSubproductsAndInstallations}
          subproducts={versions[activeVersionIndex].subproducts}
        />
      </div>
    );
  }

  return (
    // Fields whose text does not fit show the whole text when the pointer is on them
    // or when they get the focus
    <div
      className="calculation-window"
      onFocus={showFullTextOnHover}
      onMouseOver={showFullTextOnHover}
    >
      <Header
        activeVersionIndex={activeVersionIndex}
        formState={formState}
        locked={locked}
        offerType={offerType}
        setActiveVersionIndex={setActiveVersionIndex}
        setVersions={setVersions}
        setView={setView}
        versions={versions}
      />
      {locked ? null : (
        <div className="row-order-toolbar">
          <div className="target-margin">
            <NumberWithUnitInputField
              id="target-margin"
              label={t(`${translations.calculationWindow}:Target margin`)}
              maxIntegerDigits={2}
              name="targetMarginPercent"
              onChange={handleTargetMarginChange}
              unit="%"
              value={targetMarginPercent}
            />
          </div>
          <RestoreDeletedRows
            deletedRows={deletedRowsOfVersion}
            restore={restoreDeletedRow}
          />
          {reordering ? (
            <span className="row-order-info">
              {t(`${translations.calculationWindow}:Row order info`)}
            </span>
          ) : null}
          <PrimaryButton
            className={reordering ? 'row-order-active' : undefined}
            onClick={() => setReordering(!reordering)}
            size="sm"
          >
            {reordering
              ? t(`${translations.calculationWindow}:Row order done`)
              : t(`${translations.calculationWindow}:Change row order`)}
          </PrimaryButton>
        </div>
      )}
      <Subproducts
        locked={locked}
        onRemoveDetail={keepDeletedDetail}
        onRemoveSubproduct={keepDeletedSubproduct}
        quantity={numericStringToFloat(formState.quantity)}
        reordering={isReordering}
        setSubproducts={setSubproducts}
        subproducts={versions[activeVersionIndex].subproducts}
        targetMarginPercent={targetMarginPercent}
      />
      {installationCategoriesLoaded ? (
        <>
          <Installations
            locked={locked}
            installations={versions[activeVersionIndex].installations}
            onRemoveInstallation={keepDeletedInstallation}
            quantity={numericStringToFloat(formState.quantity)}
            reordering={isReordering}
            setInstallations={setInstallations}
            targetMarginPercent={targetMarginPercent}
          />
          <OtherExpenses
            locked={locked}
            installations={versions[activeVersionIndex].installations}
            onRemoveInstallation={keepDeletedInstallation}
            quantity={numericStringToFloat(formState.quantity)}
            reordering={isReordering}
            setInstallations={setInstallations}
            targetMarginPercent={targetMarginPercent}
          />
        </>
      ) : (
        loadingInstallations
      )}
      <hr />
      <Summary
        installations={versions[activeVersionIndex].installations}
        productUnit={formState.unit}
        quantity={numericStringToFloat(formState.quantity)}
        subproducts={versions[activeVersionIndex].subproducts}
      />
      <hr />
      {installationCategoriesLoaded ? (
        <>
          <InstallationContract
            locked={locked}
            productUnit={formState.unit}
            quantity={numericStringToFloat(formState.quantity)}
            setVersion={setVersion}
            version={versions[activeVersionIndex]}
          />
          <hr />
          <BatchCalculationReviews
            locked={locked}
            productUnit={formState.unit}
            quantity={numericStringToFloat(formState.quantity)}
            setCalculationReviews={setCalculationReviews}
            version={versions[activeVersionIndex]}
            calculationReviews={
              versions[activeVersionIndex].calculationReviews ?? []
            }
          />
        </>
      ) : (
        loadingInstallations
      )}
      <hr />
      {/* A price list row has only its memo: no alternatives, offer text or images */}
      {formState.isPriceListItem === true ? (
        <div className="version-details">
          <div className="version-details-internal">
            <p className="version-details-title">
              {t(`${translations.calculationWindow}:Internal`)}
              <span className="version-details-tag internal">
                {t(`${translations.calculationWindow}:Not printed`)}
              </span>
            </p>
            <div className="version-details-box">
              <Memo memo={memo} onTextInputChanged={setMemo} locked={locked} />
            </div>
          </div>
        </div>
      ) : (
        <>
          <h2>{t(`${translations.calculationWindow}:Version details`)}</h2>
          {/* Left: internal. Right: what is printed in the offer, in the order of the PDF. */}
          <div className="version-details">
            <div className="version-details-internal">
              <p className="version-details-title">
                {t(`${translations.calculationWindow}:Internal`)}
                <span className="version-details-tag internal">
                  {t(`${translations.calculationWindow}:Not printed`)}
                </span>
              </p>
              <div className="version-details-box">
                <Memo
                  memo={memo}
                  onTextInputChanged={setMemo}
                  locked={locked}
                />
              </div>
              <VersionActions
                activeVersionIndex={activeVersionIndex}
                locked={locked}
                setActiveVersionIndex={setActiveVersionIndex}
                setVersions={setVersions}
                versions={versions}
              />
              <ReferencePrices
                name={formState.name}
                offerProductId={offerProductId}
                offerType={offerType}
                unit={formState.unit}
              />
            </div>
            <div className="version-details-printed">
              <p className="version-details-title">
                {t(`${translations.calculationWindow}:Printed in offer`)}
                <span className="version-details-tag">PDF</span>
              </p>
              <Version
                activeVersionIndex={activeVersionIndex}
                locked={locked}
                offerText={versions[activeVersionIndex].offerText}
                onOfferTextChanged={onOfferTextChanged}
                setVersions={setVersions}
                versions={versions}
              />
              {/* Images of the active alternative, printed after its text in the offer PDF */}
              {offerProductId !== undefined &&
              versions[activeVersionIndex].id > 0 ? (
                <OfferProductImages
                  key={versions[activeVersionIndex].id}
                  locked={locked}
                  offerProductId={offerProductId}
                  versionId={versions[activeVersionIndex].id}
                />
              ) : (
                <p className="offer-product-images-info">
                  {t(
                    `${translations.offers}:Save alternative before adding images`
                  )}
                </p>
              )}
            </div>
          </div>
        </>
      )}
      <div className="calculation-window-action-bar">
        <span>
          <PrimaryButton
            className="button-one-line outline-button"
            onClick={close}
          >
            {t(`${translations.common}:Cancel`).toLocaleUpperCase()}
          </PrimaryButton>
        </span>
        <span className="calculation-window-action-bar-right">
          {/* The calculation as a product configuration, also from a locked offer */}
          {canSaveConfigurations && formState.isPriceListItem !== true ? (
            <PrimaryButton
              className="button-one-line outline-button"
              onClick={() => setSaveAsConfigurationOpen(true)}
            >
              {t(
                `${translations.calculationWindow}:saveAsConfiguration.Button`
              ).toLocaleUpperCase()}
            </PrimaryButton>
          ) : null}
          {openPreview === undefined ? null : (
            <PrimaryButton
              className="button-one-line outline-button"
              onClick={openPreview}
            >
              {t(`${translations.offers}:pdfPreview.Open`).toLocaleUpperCase()}
            </PrimaryButton>
          )}
          {locked ? null : (
            <>
              <PrimaryButton className="button-one-line" onClick={saveData}>
                {t(`${translations.common}:Save`).toLocaleUpperCase()}
              </PrimaryButton>
              <PrimaryButton className="button-one-line" onClick={ready}>
                {t(`${translations.common}:Ready`).toLocaleUpperCase()}
              </PrimaryButton>
            </>
          )}
        </span>
      </div>
      <SaveAsConfiguration
        defaultName={`${formState.position} ${formState.name}`.trim()}
        isOpen={saveAsConfigurationOpen}
        toggle={() => setSaveAsConfigurationOpen(!saveAsConfigurationOpen)}
        version={() =>
          formatToApiFormat({
            ...formState,
            versions: [versions[activeVersionIndex]],
          }).versions[0]
        }
      />
    </div>
  );
};

export default CalculationWindow;
